import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { NavLink, useLocation } from 'react-router-dom'
import { routePaths } from '../route_paths'
import { IRootState } from '../store'
import { ILoggedUser } from '../typeScript/interfaces/IUser'
import { roleUtils } from '../utils/roleUtils'

import ArrowDown from '../assets/ArrowDown.svg'
import ExternalLink from '../assets/ExternalLink.svg'

export type Props = {
  handleLogout: () => void
  handleSwitchPortal: () => void
  isPortal: boolean
}

function ProfileDropdown(props: Props) {
  const { handleLogout, handleSwitchPortal, isPortal } = props
  const { t } = useTranslation(['nav'])
  const [open, setOpen] = useState<boolean>(false)
  const user = useSelector<IRootState, ILoggedUser | null>(
    (state) => state.user.user
  )
  const location = useLocation()

  // Check if current user has manager role from JWT token
  const currentUser = roleUtils.getCurrentUser()
  const isManager = roleUtils.isManager(currentUser)

  const onWindowClick = (_e: MouseEvent) => {
    setOpen(false)
  }

  const onClick = (e) => {
    e.stopPropagation()
    setOpen(!open)
  }

  useEffect(() => {
    window.addEventListener('click', onWindowClick)

    return () => {
      window.removeEventListener('click', onWindowClick)
    }
  }, [])

  return (
    <div className={'profile-dropdown'} onClick={onClick}>
      {isPortal && (
        <>
          <span
            className={
              'header ' +
              (location.pathname === routePaths.portalProfile
                ? 'header--active'
                : '')
            }
          >
            {t('profile')}
          </span>
          <span className={open ? 'profile-dropdown--open' : ''}>
            <img src={ArrowDown} alt="ArrowDown" />
          </span>
          {open && (
            <div className="profile-dropdown--content">
              <ul>
                <li>
                  <NavLink
                    to={routePaths.portalProfile}
                    className={({ isActive }) =>
                      'profile-dropdown--link' +
                      (isActive ? ' profile-dropdown--link-active' : '')
                    }
                  >
                    {t('my_profile')}
                  </NavLink>
                </li>
                {user?.canUpdatePaymentInformation && (
                  <li>
                    <NavLink
                      to={routePaths.portalPayment}
                      className={({ isActive }) =>
                        'profile-dropdown--link' +
                        (isActive ? ' profile-dropdown--link-active' : '')
                      }
                    >
                      {t('my_payment')}
                    </NavLink>
                  </li>
                )}
                <li>
                  <NavLink
                    to={routePaths.portalTariff}
                    className={({ isActive }) =>
                      'profile-dropdown--link' +
                      (isActive ? ' profile-dropdown--link-active' : '')
                    }
                  >
                    {t('tariff')}
                  </NavLink>
                </li>
                {user?.isReimbursementActivated && (
                  <li>
                    <NavLink
                      to={routePaths.portalPayout}
                      className={({ isActive }) =>
                        'profile-dropdown--link' +
                        (isActive ? ' profile-dropdown--link-active' : '')
                      }
                    >
                      {t('payout')}
                    </NavLink>
                  </li>
                )}
                {user?.isFleetDataEnrichment && (
                  <li>
                    <NavLink
                      to={routePaths.portalFleetData}
                      className={({ isActive }) =>
                        'profile-dropdown--link' +
                        (isActive ? ' profile-dropdown--link-active' : '')
                      }
                    >
                      {t('fleetData')}
                    </NavLink>
                  </li>
                )}
                {isManager && (
                  <li>
                    <span
                      className={
                        'profile-dropdown--link flex items-center justify-between !gap-2'
                      }
                      onClick={handleSwitchPortal}
                    >
                      <span>{t('switch_portal')}</span>
                      <span className="inline-flex items-center justify-center ml-2 h-8 w-8 rounded-[100%] bg-bg_ligth-grey-gradient p-2">
                        <img src={ExternalLink} alt="Switch portal" />
                      </span>
                    </span>
                  </li>
                )}
                <li>
                  <span
                    className={'profile-dropdown--link'}
                    onClick={handleLogout}
                  >
                    {t('log_out')}
                  </span>
                </li>
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default ProfileDropdown
